import { HttpService } from "../http"
import { ApiRoutes } from "../routes"
import { SessionService } from "../session"
import { Logger } from "../logger"
import { LoginError } from "../../models/login"
import { SupportUsers } from "../../models/common/supportusers"
import { CreateSupportUsers } from "../../models/transport/request/createSupportUsers"
import { SupportUserBriefResponse } from "../../models/transport/response/supportUserBrief"
import { UpdateSupportUserRequest } from "../../models/transport/request/UpdateSupportUserRequest"
import { container } from "../../../infrastructure/container"

/**
 * Interface defining CRUD operations for Support Users.
 */
export interface SupportUserService {
    /** Create a new support user. */
    createSupportUsers(request: CreateSupportUsers): Promise<SupportUsers>
    /** List support users (brief). */
    getSupportUsers(): Promise<SupportUserBriefResponse[]>
    /** Update support users. */
    updateSupportUser(data: UpdateSupportUserRequest): Promise<SupportUsers>
    /** Delete support users. */
    deleteSupportUsers(id: string): Promise<{ message: string }>
}

/**
 * Implementation of SupportUserService that interacts with the backend API.
 */
export class AppSupportUserService implements SupportUserService {
    /**
     * Initializes the login service with the HTTP service instance.
     * @param httpService - The HTTP service to send API requests.
     */
    private httpService = container.resolve<HttpService>("httpService")
    private sessionService = container.resolve<SessionService>("sessionService")
    private logger = container.resolve<Logger>("logger")

    // -------------------------------------------------------------
    // CREATE
    // -------------------------------------------------------------
    async createSupportUsers(request: CreateSupportUsers): Promise<SupportUsers> {
        try {
            const created = await this.httpService.post<
                CreateSupportUsers,
                SupportUsers,
                LoginError
            >(ApiRoutes.CREATE_SUPPORT_USERS, request)

            this.sessionService.addSupportUsers(created)
            console.log(created)

            const brief: SupportUserBriefResponse = {
                id: created.email,
                name: `${created.firstname} ${created.lastname ?? ""}`.trim(),
                email: created.email,
                role: created.role,
                franchises: (created.franchises ?? []).map((f) => f.franchiseId),
                isActive: created.isActive
            }
            const updatedBrief = [...this.sessionService.supportUserBrief, brief]
            this.sessionService.setSupportUserBrief(updatedBrief)

            return created
        } catch (error) {
            this.logger.error("Error creating support user:", error)
            throw error
        }
    }

    // -------------------------------------------------------------
    // Get
    // -------------------------------------------------------------
    async getSupportUsers(): Promise<SupportUserBriefResponse[]> {
        try {
            const fullUsers = await this.httpService.get<SupportUsers[], LoginError>(
                ApiRoutes.GET_SUPPORT_USERS
            )

            // Convert to brief model – use email as a stable unique key
            const briefList: SupportUserBriefResponse[] = fullUsers.map((u) => ({
                id: u.email, // email is unique – backend does not send explicit id
                name: `${u.firstname} ${u.lastname ?? ""}`.trim(),
                email: u.email,
                role: u.role,
                franchises: (u.franchises ?? []).map((f) => ({
                    franchiseId: f.franchiseId,
                    name: f.name
                })),
                isActive: u.isActive
            }))

            this.sessionService.setSupportUserBrief(briefList)
            console.log(briefList)
            return briefList
        } catch (error) {
            this.logger.error("Error fetching support users:", error)
            throw error
        }
    }

    async updateSupportUser(data: UpdateSupportUserRequest): Promise<SupportUsers> {
        try {
            const updated = await this.httpService.post<
                UpdateSupportUserRequest,
                SupportUsers,
                LoginError
            >(ApiRoutes.UPDATE_SUPPORT_USERS, data)
            console.log(updated)
            return updated
        } catch (error) {
            this.logger.error("Error updating support user:", error)
            throw error
        }
    }

    async deleteSupportUsers(email: string): Promise<{ message: string }> {
        try {
            const route = `${ApiRoutes.DELETE_SUPPORT_USERS}?email=${encodeURIComponent(email)}`
            const response = await this.httpService.delete<{ message: string }, LoginError>(route)
            this.logger.info("Support user deleted:", response.message)
            return response
        } catch (error) {
            this.logger.error("Error deleting support user:", error)
            throw error
        }
    }
}